import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  CalendarMonthOutlined,
  CheckCircleOutline,
  CurrencyRupeeOutlined,
  DateRangeOutlined,
  FileDownloadOutlined,
  PercentOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette, detailCardSx } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import { designColors, designShell, designType, designWeights } from "services/constants/designTokens";
import { esiRegisterHeader } from "pages/PayRoll/PayRollHeaders";
import { FormSection, panelBodySx, Field } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { SimpleTable, ToneChip, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { ChoiceCard } from "pages/PayRoll/Shared/statutoryConfigUi";
import { RecordHeaderCard, StatutoryList, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { EMPLOYEES, FINALISED_MONTHS, codeName, formatDay, historyFor, money, monthLabel, monthRow, num } from "./esiReportsData";

// Contribution Register (R2) with its drawer — This Month and History
// (R2a) — row checkboxes (R2b) and Download for a Period (R2c). Read-only
// views of the finalised payroll; whole rupees everywhere.

const { indigo, pink, green, orange, purple } = detailAccentPalette;
const NUMBER_KEYS = new Set(["days", "wage", "ee", "er", "total"]);
const COVERAGE_TONES = { Covered: "green", "Covered · continuing": "blue", "Covered · disability": "green", "Covered · override": "purple" };
const tagOf = (label) => (label === "Covered" ? null : label.replace("Covered · ", ""));

const breakdownText = (breakdown) =>
  [
    ["Basic", breakdown.BASIC_E],
    ["DA", breakdown.DA_E],
    ["Special", breakdown.SPECIAL_E],
    ["Overtime", breakdown.OT],
    ["Shift", breakdown.SHIFT],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => `${label} ${num(value)}`)
    .join(" + ");

const ContributionPanel = ({ open, row, monthInfo, onClose }) => {
  const writers = useExcelDownload({ maxPdfRows: 5000 });
  const [tab, setTab] = useState("month");
  useEffect(() => {
    if (open) setTab("month");
  }, [open, row?.id]);
  if (!row) return null;
  const version = row.version;
  const eeComponent = version?.components.find((component) => component.code === "EMP_ESI");
  const erComponent = version?.components.find((component) => component.code === "EMPR_ESI");
  const history = historyFor(row.id, row.month);
  const historyTotals = history.reduce((sum, item) => ({ ee: sum.ee + item.ee, er: sum.er + item.er }), { ee: 0, er: 0 });
  const lowWage = row.covered && row.ee === 0 && row.days > 0;
  const raw = (rate) => Math.round(row.wage * Number(rate) + Number.EPSILON) / 100;

  const downloadHistory = () =>
    writers.downloadExcel(
      history.map((item) => ({ month: monthLabel(item.month), days: item.days, wage: item.wage, ee: item.ee, er: item.er, total: item.total, coverage: item.coverage_label })),
      [
        { name: "Month", key: "month" },
        { name: "Days", key: "days" },
        { name: "ESI Wage", key: "wage" },
        { name: "EE", key: "ee" },
        { name: "ER", key: "er" },
        { name: "Total", key: "total" },
        { name: "Coverage", key: "coverage" },
      ],
      `ESI History ${row.name}`,
      {},
      { Employee: `${row.name} (${row.emp_id})`, "IP Number": row.ip_number || "—", Period: row.coverage ? `${formatDay(row.coverage.from)} → ${formatDay(row.coverage.until)}` : "—" },
      [],
    );

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="ESI Contribution"
      subtitle={tab === "month" ? `${monthInfo.label} · from finalised payroll v${monthInfo.version}` : `${row.name} · from finalised payrolls`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(row.name)}
          title={row.name}
          chip={<ToneChip label={row.coverage_label} tone={COVERAGE_TONES[row.coverage_label] || "slate"} />}
          meta={`${row.emp_id} · IP ${row.ip_number || "missing"} · ${codeName(row.code)}`}
        />
        <Mui.Tabs value={tab} onChange={(event, next) => setTab(next)} sx={{ ...designShell.panelTabs, mt: 0, borderBottom: `1px solid ${designColors.slate200}` }}>
          <Mui.Tab value="month" label="This Month" />
          <Mui.Tab value="history" label="History" />
        </Mui.Tabs>
        {tab === "month" ? (
          <>
            <RequestDetailsSection
              title="CALCULATION"
              fields={[
                { icon: CalendarMonthOutlined, label: "Days paid", value: `${row.days} of ${row.dim}`, accent: indigo },
                { icon: CurrencyRupeeOutlined, label: "ESI contribution wage", value: money(row.wage), helper: breakdownText(row.breakdown) || "No earnings this month", accent: green, multiline: true },
                {
                  icon: PercentOutlined,
                  label: `Employee ${eeComponent?.rate_pct || "0.75"}%`,
                  value: money(row.ee),
                  helper: lowWage ? `Average daily wage ₹${row.avg.toFixed(2)} ≤ ₹${eeComponent?.low_wage_limit} — low-wage exemption` : `${raw(eeComponent?.rate_pct || 0.75).toFixed(2)} rounded up`,
                  accent: purple,
                },
                { icon: PercentOutlined, label: `Employer ${erComponent?.rate_pct || "3.25"}%`, value: money(row.er), helper: `${raw(erComponent?.rate_pct || 3.25).toFixed(2)} rounded up`, accent: orange },
              ]}
            />
            <RequestDetailsSection
              title="COVERAGE"
              fields={[
                {
                  icon: TimerOutlined,
                  label: "Covered until",
                  value: formatDay(row.coverage.until),
                  helper:
                    row.coverage.reason === "CONTINUING"
                      ? `Wage at period start ${money(row.coverage.wage_at_test)} — continues although now above ${money(row.coverage.ceiling)}`
                      : row.coverage.rule,
                  accent: pink,
                  multiline: true,
                },
              ]}
            />
            <RequestDetailsSection
              title="DEDUCTION"
              fields={[
                {
                  icon: CheckCircleOutline,
                  label: "Deducted in payroll",
                  value: row.ee ? `Yes · payslip ${monthInfo.label}` : "Nothing to deduct",
                  helper: "Payment is tracked per ESI code in Reconciliation, not per employee",
                  accent: green,
                },
              ]}
            />
          </>
        ) : (
          <Mui.Stack spacing={1.5}>
            <Mui.Stack direction="row" justifyContent="space-between" alignItems="center">
              <ToneChip label={`Period: ${history.length ? `${monthLabel(history[history.length - 1].month)} – ${monthLabel(history[0].month)}` : "—"}`} tone="slate" />
              <SecondaryButton startIcon={<FileDownloadOutlined />} onClick={downloadHistory} disabled={!history.length}>
                Download
              </SecondaryButton>
            </Mui.Stack>
            <SimpleTable
              fit
              heads={[
                { label: "Month", width: 80 },
                { label: "Days", align: "right", width: 50 },
                { label: "ESI Wage", align: "right", width: 80 },
                { label: "EE", align: "right", width: 50 },
                { label: "ER", align: "right", width: 60 },
                { label: "Total", align: "right", width: 60 },
                { label: "Coverage", width: 100 },
              ]}
            >
              {history.map((item) => (
                <Mui.TableRow key={item.month}>
                  <Mui.TableCell sx={tableCellSx}>{monthLabel(item.month)}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{item.days}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{num(item.wage)}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{num(item.ee)}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{num(item.er)}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{num(item.total)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>
                    <ToneChip label={tagOf(item.coverage_label) || "Covered"} tone={COVERAGE_TONES[item.coverage_label] || "green"} />
                  </Mui.TableCell>
                </Mui.TableRow>
              ))}
              <Mui.TableRow>
                <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{history.length} months</Mui.TableCell>
                <Mui.TableCell colSpan={6} align="right" sx={{ ...tableCellSx, fontWeight: designWeights.heading }}>
                  EE {money(historyTotals.ee)} · ER {money(historyTotals.er)} · Total {money(historyTotals.ee + historyTotals.er)}
                </Mui.TableCell>
              </Mui.TableRow>
            </SimpleTable>
            <RequestInfoBanner body="Read-only, from finalised payroll snapshots. Use the Payroll Month filter for earlier contribution periods." />
          </Mui.Stack>
        )}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---- Download for a Period (R2c) ----

const MONTH_OPTIONS = [...FINALISED_MONTHS, "2025-04", "2025-05", "2025-06", "2025-07", "2025-08", "2025-09"]
  .sort()
  .map((month) => ({ value: month, label: monthLabel(month) }));
const lastMonth = FINALISED_MONTHS[FINALISED_MONTHS.length - 1];
const QUICK_PICKS = [
  { key: "6", label: "Last 6 months", from: FINALISED_MONTHS[FINALISED_MONTHS.length - 6], to: lastMonth },
  { key: "12", label: "Last 12 months", from: FINALISED_MONTHS[0], to: lastMonth },
  { key: "period", label: "Apr – Sep 2026", from: "2026-04", to: "2026-09" },
  { key: "fy", label: "FY 2025-26", from: "2025-04", to: "2026-03" },
];

const monthsBetween = (from, to) => {
  const months = [];
  for (let cursor = moment(`${from}-01`); !cursor.isAfter(`${to}-01`); cursor.add(1, "month")) months.push(cursor.format("YYYY-MM"));
  return months;
};

const DownloadPeriodPanel = ({ open, selectedIds, matchingIds, companyName, onClose }) => {
  const writers = useExcelDownload({ maxPdfRows: 5000 });
  const [form, setForm] = useState({ from: QUICK_PICKS[1].from, to: QUICK_PICKS[1].to, who: "selected", layout: "columns", format: "excel" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm((current) => ({ ...current, who: selectedIds.length ? "selected" : "matching" }));
  }, [open, selectedIds.length]);

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const ids = form.who === "selected" ? selectedIds : matchingIds;
  const rangeError = moment(`${form.from}-01`).isAfter(`${form.to}-01`) ? "From must be before To" : "";
  const months = rangeError ? [] : monthsBetween(form.from, form.to);
  const included = months.filter((month) => FINALISED_MONTHS.includes(month));
  const excluded = months.filter((month) => !FINALISED_MONTHS.includes(month));
  const people = EMPLOYEES.filter((employee) => ids.includes(employee.id));

  const data = useMemo(() => {
    if (!open) return { rows: [], headers: [] };
    const perMonth = people.flatMap((employee) => included.map((month) => monthRow(employee, month)).filter((row) => row && row.covered));
    if (form.layout === "rows") {
      return {
        rows: perMonth.map((row) => ({ name: row.name, emp_id: row.emp_id, ip: row.ip_number, month: monthLabel(row.month), days: row.days, wage: row.wage, ee: row.ee, er: row.er, total: row.total })),
        headers: [
          { name: "Employee", key: "name" },
          { name: "Employee ID", key: "emp_id" },
          { name: "IP Number", key: "ip" },
          { name: "Month", key: "month" },
          { name: "Days", key: "days" },
          { name: "ESI Wage", key: "wage" },
          { name: "EE", key: "ee" },
          { name: "ER", key: "er" },
          { name: "Total", key: "total" },
        ],
      };
    }
    return {
      rows: people
        .map((employee) => {
          const own = perMonth.filter((row) => row.id === employee.id);
          if (!own.length) return null;
          return {
            name: employee.name,
            ip: employee.ip_number,
            ...Object.fromEntries(included.map((month) => [month, own.find((row) => row.month === month)?.total ?? 0])),
            total: own.reduce((sum, row) => sum + row.total, 0),
          };
        })
        .filter(Boolean),
      headers: [{ name: "Employee", key: "name" }, { name: "IP", key: "ip" }, ...included.map((month) => ({ name: monthLabel(month), key: month })), { name: "Total", key: "total" }],
    };
  }, [open, people, included, form.layout]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    if (rangeError || !data.rows.length || busy) return;
    setBusy(true);
    try {
      const writer = form.format === "pdf" ? writers.downloadPdf : writers.downloadExcel;
      await writer(
        data.rows,
        data.headers,
        `ESI Contribution ${monthLabel(form.from)} - ${monthLabel(form.to)}`,
        {},
        {
          "Company Name": companyName || "N/A",
          Months: `${monthLabel(form.from)} – ${monthLabel(form.to)}`,
          ...(excluded.length ? { "Excluded (no finalised payroll)": excluded.map(monthLabel).join(", ") } : {}),
          Employees: `${people.length} (${form.who === "selected" ? "selected" : "all matching filters"})`,
          "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
        },
        [],
      );
      onClose();
    } catch (error) {
      showToast("error", error.message || "Could not download.");
    } finally {
      setBusy(false);
    }
  };

  const preview = data.rows.slice(0, 2);
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Download for a Period"
      subtitle="ESI Contribution Register"
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel={busy ? "Downloading..." : "Download"} primaryProps={{ onClick: download, disabled: busy || Boolean(rangeError) || !data.rows.length }} />}
    >
      <Mui.Stack spacing={2}>
        <FormSection title="Months">
          <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ gridColumn: "1 / -1" }}>
            {QUICK_PICKS.map((pick) => {
              const active = form.from === pick.from && form.to === pick.to;
              return (
                <Mui.Chip
                  key={pick.key}
                  label={pick.label}
                  onClick={() => setForm((current) => ({ ...current, from: pick.from, to: pick.to }))}
                  variant={active ? "filled" : "outlined"}
                  sx={{ fontWeight: designWeights.semibold, ...(active ? { backgroundColor: designColors.tealDark, color: designColors.white, "&:hover": { backgroundColor: designColors.tealDark } } : {}) }}
                />
              );
            })}
          </Mui.Stack>
          <Field label="From month" error={rangeError || undefined}>
            <SelectField value={form.from} onChange={(value) => value && set("from")(value)} options={MONTH_OPTIONS} />
          </Field>
          <Field label="To month">
            <SelectField value={form.to} onChange={(value) => value && set("to")(value)} options={MONTH_OPTIONS} />
          </Field>
          <Mui.Typography sx={{ ...designType.captionText, gridColumn: "1 / -1" }}>
            Only finalised payroll months are included.{excluded.length ? ` Excluded: ${excluded.map(monthLabel).join(", ")}.` : ""}
          </Mui.Typography>
        </FormSection>
        <FormSection title="Employees">
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ gridColumn: "1 / -1" }}>
            <ChoiceCard label={`Selected (${selectedIds.length})`} hint="ticked in the register" selected={form.who === "selected"} disabled={!selectedIds.length} onClick={() => set("who")("selected")} />
            <ChoiceCard label="All matching filters" hint={`${matchingIds.length} employees`} selected={form.who === "matching"} onClick={() => set("who")("matching")} />
          </Mui.Stack>
        </FormSection>
        <FormSection title="Layout">
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ gridColumn: "1 / -1" }}>
            <ChoiceCard label="Row per employee per month" hint="for checking / audit" selected={form.layout === "rows"} onClick={() => set("layout")("rows")} />
            <ChoiceCard label="Row per employee" hint="month columns + total" selected={form.layout === "columns"} onClick={() => set("layout")("columns")} />
          </Mui.Stack>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            {preview.length ? (
              <Mui.Box sx={{ overflowX: "auto" }}>
                <SimpleTable heads={data.headers.slice(0, 5).map((head) => ({ label: head.name, width: 90 }))} minWidth={420}>
                  {preview.map((row, index) => (
                    <Mui.TableRow key={index}>
                      {data.headers.slice(0, 5).map((head) => (
                        <Mui.TableCell key={head.key} sx={tableCellSx}>
                          {typeof row[head.key] === "number" ? num(row[head.key]) : row[head.key] || "—"}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  ))}
                </SimpleTable>
                <Mui.Typography sx={{ ...designType.captionText, mt: 0.75 }}>
                  Preview · {num(data.rows.length)} rows{data.headers.length > 5 ? ` · ${data.headers.length - 5} more columns` : ""}
                </Mui.Typography>
              </Mui.Box>
            ) : (
              <Mui.Typography sx={designType.helperText}>No ESI rows for these employees and months.</Mui.Typography>
            )}
          </Mui.Box>
        </FormSection>
        <FormSection title="Format">
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ gridColumn: "1 / -1" }}>
            <ChoiceCard label="Excel (.xlsx)" selected={form.format === "excel"} onClick={() => set("format")("excel")} />
            <ChoiceCard label="PDF" selected={form.format === "pdf"} onClick={() => set("format")("pdf")} />
          </Mui.Stack>
        </FormSection>
        <RequestInfoBanner
          body={`${people.length} employees × ${included.length} months. Exports over 5,000 rows run in the background — you'll get a notification when the file is ready.`}
        />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---- Tab ----

export const EsiRegisterTab = ({ rows, monthInfo }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [coverage, setCoverage] = useState("covered");
  const [viewId, setViewId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [periodOpen, setPeriodOpen] = useState(false);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "esi_contribution_register_saved_views",
    fullHeaders: esiRegisterHeader,
  });
  const shown = coverage === "covered" ? rows.filter((row) => row.covered) : rows;
  const sum = (key) => shown.reduce((total, row) => total + row[key], 0);

  const renderCell = (row, key) => {
    if (key === "name") {
      const tag = tagOf(row.coverage_label);
      return (
        <Mui.Stack direction="row" spacing={1} alignItems="center">
          <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>
            {initialsOf(row.name)}
          </Mui.Avatar>
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          {tag && row.covered ? <ToneChip label={tag} tone={COVERAGE_TONES[row.coverage_label] || "slate"} /> : null}
          {!row.covered ? <ToneChip label="not covered" tone="slate" /> : null}
        </Mui.Stack>
      );
    }
    if (key === "ip_label") return row.ip_number || <Mui.Box component="span" sx={{ color: "#DC2626" }}>Missing</Mui.Box>;
    if (key === "code_label") return row.code ? row.code_label : <Mui.Box component="span" sx={{ color: "#DC2626" }}>Not linked</Mui.Box>;
    if (key === "total") return <Mui.Box component="span" sx={{ fontWeight: designWeights.semibold }}>{num(row.total)}</Mui.Box>;
    if (NUMBER_KEYS.has(key)) return num(row[key]);
    return row[key] || "—";
  };

  return (
    <>
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}>
        <Mui.Box sx={{ width: { xs: "100%", sm: 220 } }}>
          <SelectField
            value={coverage}
            onChange={(value) => value && setCoverage(value)}
            options={[
              { value: "covered", label: "Coverage: Covered" },
              { value: "all", label: "Coverage: All employees" },
            ]}
          />
        </Mui.Box>
        {selected.length ? (
          <Mui.Stack direction="row" spacing={1.5} alignItems="center">
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{selected.length} selected</Mui.Typography>
            <Mui.Box component="button" type="button" onClick={() => setSelected([])} sx={{ border: 0, background: "none", p: 0, cursor: "pointer", color: designColors.tealDark, fontWeight: designWeights.semibold, font: "inherit" }}>
              Clear
            </Mui.Box>
          </Mui.Stack>
        ) : null}
      </Mui.Stack>
      <StatutoryList
        title={`ESI Contribution Register ${monthInfo.label}`}
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={shown}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel={coverage === "covered" ? "covered employees" : "employees"}
        searchPlaceholder="Search name, ID or IP number"
        selection={{ keys: selected, onChange: setSelected }}
        metadata={{
          "Company Name": topbarCompanyName || "N/A",
          "Payroll Month": monthInfo.label,
          "Payroll Version": monthInfo.version,
          Coverage: coverage === "covered" ? "Covered" : "All employees",
          "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
        }}
        action={
          <SecondaryButton startIcon={<DateRangeOutlined />} onClick={() => setPeriodOpen(true)} sx={{ minHeight: 40, whiteSpace: "nowrap", flexShrink: 0 }}>
            Download for a period
          </SecondaryButton>
        }
      />
      <Mui.Stack spacing={1.5} sx={{ mt: 1.5 }}>
        <Mui.Box sx={{ ...detailCardSx, py: 1.25 }}>
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, textAlign: "right" }}>
            Totals: wages {money(sum("wage"))} · employee {money(sum("ee"))} · employer {money(sum("er"))} · total {money(sum("total"))}
          </Mui.Typography>
        </Mui.Box>
        <RequestInfoBanner body="Days paid (not NCP days) and whole rupees, from the finalised payroll. Downloads use the same rows and totals as the screen." />
      </Mui.Stack>
      <ContributionPanel open={Boolean(viewId)} row={rows.find((row) => row.id === viewId)} monthInfo={monthInfo} onClose={() => setViewId(null)} />
      <DownloadPeriodPanel
        open={periodOpen}
        selectedIds={selected}
        matchingIds={shown.map((row) => row.id)}
        companyName={topbarCompanyName}
        onClose={() => setPeriodOpen(false)}
      />
    </>
  );
};
